/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { rolesAPI } from '../../api/crm'
import { useEntityTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { SKIP_GUARD, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import { useNotify } from '../../components/common/NotificationProvider'
import FieldAccessDialog from '../../components/roles/FieldAccessDialog'
import { PRESETS, RANK, SCOPES, clamp, emptyRole } from '../../components/roles/roleModel'
import { entityLabelPlural } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'

const DEFAULTS = '__defaults__'

const ScopeSelect = ({ value, onChange, max, label }) => (
  <TextField select value={value} onChange={(e) => onChange(e.target.value)} fullWidth={false} sx={{ minWidth: 140 }} inputProps={{ 'aria-label': label }}>
    {SCOPES.map((s) => <MenuItem key={s.value} value={s.value} disabled={max && RANK[s.value] > RANK[max]}>{s.label}</MenuItem>)}
  </TextField>
)

/** Name, default flag, and a permission row per record type (+ one for types without their own row). */
const RoleEditPage = () => {
  const { roleId } = useParams()
  const isNew = !roleId
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const notify = useNotify()
  const queryClient = useQueryClient()
  const { entityTypes } = useEntityTypes()
  const { data: saved, isLoading } = useQuery({ queryKey: [tenantId, 'roles', roleId], queryFn: () => rolesAPI.get(roleId), enabled: !isNew })
  const [role, setRole] = useState(null)
  const [initial, setInitial] = useState(null)
  const [fieldsFor, setFieldsFor] = useState(null)
  useEffect(() => {
    if (role) return
    const start = isNew ? emptyRole() : saved && { name: saved.name, description: saved.description || '', isDefault: saved.isDefault, permissions: saved.permissions }
    if (start) { setRole(start); setInitial(start) }
  }, [isNew, saved, role])
  const guard = useUnsavedChangesGuard(!!role && JSON.stringify(role) !== JSON.stringify(initial))

  const save = useMutation({
    mutationFn: (body) => (isNew ? rolesAPI.create(body) : rolesAPI.update(roleId, body)),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'roles'] })
      queryClient.invalidateQueries({ queryKey: [tenantId, 'members'] })
      notify(isNew ? `Role “${r.name}” created` : 'Role saved')
      navigate('/settings/roles', SKIP_GUARD)
    },
  })

  if (!role || (!isNew && isLoading)) return <Loading />

  const perms = role.permissions
  const rowOf = (type) => (type === DEFAULTS ? perms.defaults : perms.entities[type] || { ...perms.defaults, fields: {} })
  const setRow = (type, patch) => {
    const next = clamp({ ...rowOf(type), ...patch })
    setRole((r) => ({
      ...r,
      permissions: type === DEFAULTS
        ? { ...r.permissions, defaults: { ...next, fields: {} } }
        : { ...r.permissions, entities: { ...r.permissions.entities, [type]: next } },
    }))
  }
  const applyPreset = (preset) => setRole((r) => ({
    ...r,
    permissions: {
      defaults: { ...preset, fields: {} },
      entities: Object.fromEntries(entityTypes.map((t) => [t, { ...preset, fields: r.permissions.entities[t]?.fields || {} }])),
    },
  }))
  // every listed type is saved explicitly, so later changes to "other types" don't change them
  const body = () => ({
    ...role,
    name: role.name.trim(),
    permissions: { defaults: perms.defaults, entities: Object.fromEntries(entityTypes.map((t) => [t, rowOf(t)])) },
  })
  const errors = save.error ? getErrorList(save.error) : []

  const row = (type, label) => {
    const p = rowOf(type)
    const restricted = Object.keys(p.fields || {}).length
    return (
      <TableRow key={type}>
        <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</TableCell>
        <TableCell><ScopeSelect value={p.view} onChange={(view) => setRow(type, { view })} label={`${label}: view`} /></TableCell>
        <TableCell align="center">
          <Checkbox checked={!!p.create} onChange={(e) => setRow(type, { create: e.target.checked })} inputProps={{ 'aria-label': `${label}: create` }} />
        </TableCell>
        <TableCell><ScopeSelect value={p.edit} max={p.view} onChange={(edit) => setRow(type, { edit })} label={`${label}: edit`} /></TableCell>
        <TableCell><ScopeSelect value={p.delete} max={p.view} onChange={(del) => setRow(type, { delete: del })} label={`${label}: delete`} /></TableCell>
        <TableCell>
          {type === DEFAULTS ? <Typography variant="caption" color="text.secondary">—</Typography> : (
            <Button size="small" onClick={() => setFieldsFor(type)} disabled={p.view === 'NONE'}>
              {restricted ? `${restricted} restricted` : 'All editable'}
            </Button>
          )}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Box sx={{ maxWidth: 1000 }}>
      {guard}
      <PageHeader title={isNew ? 'New role' : role.name || 'Role'} subtitle="Own records: ones the member created, or where they’re in a person field such as Owner"
        actions={(
          <>
            <Button onClick={() => navigate('/settings/roles')}>Cancel</Button>
            <Button variant="contained" disabled={save.isPending || !role.name.trim()} onClick={() => save.mutate(body())}>
              {save.isPending ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </Button>
          </>
        )} />
      {save.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(save.error)}
          {errors.length > 0 && <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{errors.map((e) => <li key={e}>{e}</li>)}</Box>}
        </Alert>
      )}
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField label="Name" required value={role.name} onChange={(e) => setRole({ ...role, name: e.target.value })}
              sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }} inputProps={{ maxLength: 60 }} autoFocus={isNew} placeholder="e.g. Sales rep" />
            <TextField label="Description" value={role.description} onChange={(e) => setRole({ ...role, description: e.target.value })}
              sx={{ flex: 1 }} inputProps={{ maxLength: 300 }} />
          </Stack>
          <FormControlLabel sx={{ mt: 1.5 }}
            control={<Switch checked={role.isDefault} disabled={!isNew && initial?.isDefault} onChange={(e) => setRole({ ...role, isDefault: e.target.checked })} />}
            label={!isNew && initial?.isDefault ? 'Default role (make another role the default to change this)' : 'Default role for members without one'} />
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Permissions</Typography>
            <Typography variant="caption" color="text.secondary">Start from:</Typography>
            {PRESETS.map((p) => <Button key={p.label} size="small" variant="outlined" onClick={() => applyPreset(p.value)}>{p.label}</Button>)}
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Record type</TableCell>
                  <TableCell>View</TableCell>
                  <TableCell align="center">Create</TableCell>
                  <TableCell>Edit</TableCell>
                  <TableCell>Delete</TableCell>
                  <TableCell>Fields</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entityTypes.map((t) => row(t, entityLabelPlural(t)))}
                {row(DEFAULTS, 'Record types added later')}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Edit and Delete can’t go further than View. Automations aren’t limited by roles.
          </Typography>
        </Paper>
      </Stack>
      <FieldAccessDialog entityType={fieldsFor} value={fieldsFor ? rowOf(fieldsFor).fields : null} onClose={() => setFieldsFor(null)}
        onSave={(fields) => { setRow(fieldsFor, { fields }); setFieldsFor(null) }} />
    </Box>
  )
}

export default RoleEditPage
